import React, { useState } from 'react';
import {
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tabs,
  Tab,
  Chip,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@uniqcamp/shared';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useToast } from '../contexts/ToastContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useStaffPermissionContext } from '../hooks/useStaffPermissionContext';
import {
  canUseAuthorityLevelsTab,
  canUseStaffListTab,
  canUseStaffRolesTab,
  canUseStaffSubjectsTab,
} from '../utils/staffFeatureAccessPolicy';
import StaffForm from '../components/staff/StaffForm';
import type { StaffFormData } from '../components/staff/StaffForm';
import SubjectsTab from '../components/staff/SubjectsTab';
import StaffRolesTab from '../components/staff/StaffRolesTab';
import AuthorityLevelsTab from '../components/staff/AuthorityLevelsTab';
import { STAFF_ROLE_TYPES, getAuthorityLevelLabel, resolveAuthorityLevelsFromSchool } from '../constants';
import { SchoolAuthorityLevelsResponse, SchoolsResponse, StaffUser, UsersResponse } from '../types';

function formatSubjects(subjects: string[] | undefined): string {
  if (!subjects?.length) return '–';
  return subjects.join(', ');
}

function StaffTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('staffList');
  const user = authService.getStoredUser();
  const schoolId = user?.schoolId;

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<StaffUser | null>(null);

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];
  const schoolSubjects: string[] = school?.settings?.subjects ?? [];
  const staffRolesList: { value: string; label: string }[] = school?.settings?.staffRoles?.length
    ? school.settings.staffRoles
    : STAFF_ROLE_TYPES;
  const authoritySchoolId = schoolId || school?._id;
  const { data: authorityLevelsData } = useQuery<SchoolAuthorityLevelsResponse>(
    ['school-authority-levels', authoritySchoolId],
    async () => {
      const res = await apiClient.get(`/schools/${authoritySchoolId}/authority-levels`);
      return res.data as SchoolAuthorityLevelsResponse;
    },
    { enabled: !!authoritySchoolId }
  );
  const authorityLevelsResolved = (authorityLevelsData?.levels || []).map((l) => ({
    value: l.value,
    label: l.label,
  }));
  const authorityLevelsList = authorityLevelsResolved.length
    ? authorityLevelsResolved
    : resolveAuthorityLevelsFromSchool(school?.settings?.authorityLevels);

  const { data } = useQuery<UsersResponse>(
    ['users', schoolId],
    async () => {
      const res = await apiClient.get('/users');
      return res.data as UsersResponse;
    },
    { enabled: !!schoolId }
  );

  const staffUsers: StaffUser[] = (data?.users || []).filter(
    (u: { role?: string }) => u.role !== 'PARENT' && u.role !== 'SUPER_ADMIN'
  );

  const createMutation = useMutation(
    async (payload: {
      staffId?: string;
      name: string;
      mobile: string;
      email: string;
      role: string;
      authorityLevel: string;
      schoolId: string;
      subjects?: string[];
    }) => {
      const res = await apiClient.post('/users', payload);
      return res.data as { staffWelcomeEmailSent?: boolean };
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['users', schoolId]);
        setAddOpen(false);
        showToast(
          'success',
          data?.staffWelcomeEmailSent
            ? 'Staff added. They were emailed a link to set their password.'
            : 'Staff added. They should use Forgot password on the staff login page to set their password.',
        );
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to add staff');
      },
    }
  );

  const updateMutation = useMutation(
    async (payload: { userId: string; staffId?: string; name: string; mobile: string; email?: string; password?: string; role: string; authorityLevel: string; subjects?: string[] }) => {
      const body: Record<string, unknown> = {
        staffId: payload.staffId,
        name: payload.name,
        mobile: payload.mobile,
        role: payload.role,
        authorityLevel: payload.authorityLevel,
        subjects: payload.subjects,
      };
      if (payload.email !== undefined) body.email = payload.email;
      if (payload.password !== undefined && payload.password !== '') body.password = payload.password;
      const res = await apiClient.put(`/users/${payload.userId}`, body);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', schoolId]);
        setEditOpen(false);
        setEditingUser(null);
        showToast('success', 'Staff updated.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to update staff');
      },
    }
  );

  const blockMutation = useMutation(
    async ({ userId, block }: { userId: string; block: boolean }) => {
      await apiClient.put(`/users/${userId}/block`, { block });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['users', schoolId]),
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to update block status');
      },
    }
  );

  const deleteMutation = useMutation(
    async (userId: string) => {
      await apiClient.delete(`/users/${userId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', schoolId]);
        showToast('success', 'Staff removed.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to delete staff');
      },
    }
  );

  const handleEdit = (u: StaffUser) => {
    setEditingUser(u);
    setEditOpen(true);
  };

  const handleDelete = (u: StaffUser) => {
    if (!window.confirm(`Remove ${u.name ?? 'this staff'}? They will no longer be able to log in.`)) return;
    deleteMutation.mutate(u._id);
  };

  const handleAddSave = (data: StaffFormData) => {
    createMutation.mutate({
      ...(data.staffId && { staffId: data.staffId }),
      name: data.name,
      mobile: data.mobile,
      email: data.email,
      role: data.role,
      authorityLevel: data.authorityLevel,
      schoolId: schoolId as string,
      ...(data.subjects?.length ? { subjects: data.subjects } : {}),
    });
  };

  const handleEditSave = (data: StaffFormData) => {
    if (!editingUser) return;
    updateMutation.mutate({
      userId: editingUser._id,
      staffId: data.staffId,
      name: data.name,
      mobile: data.mobile,
      email: data.email,
      ...(data.password ? { password: data.password } : {}),
      role: data.role,
      authorityLevel: data.authorityLevel,
      subjects: data.subjects,
    });
  };

  return (
    <Box>
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" onClick={() => setAddOpen(true)}>
            Add Staff
          </Button>
        </Box>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Staff ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Authority (top → down)</TableCell>
              <TableCell>Subject(s)</TableCell>
              <TableCell align="center">Status</TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {staffUsers.map((u: StaffUser) => (
              <TableRow key={u._id}>
                <TableCell>{u.staffId || '–'}</TableCell>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.mobile}</TableCell>
                <TableCell>{staffRolesList.find((r) => r.value === u.role)?.label || u.role}</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  {getAuthorityLevelLabel(u.authorityLevel, authorityLevelsList)}
                </TableCell>
                <TableCell>{formatSubjects(u.subjects)}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={u.isBlocked ? 'Blocked' : 'Active'}
                    color={u.isBlocked ? 'default' : 'success'}
                  />
                </TableCell>
                {canEdit && (
                  <TableCell align="right">
                    {u.role === 'ADMIN' ? (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    ) : (
                      <>
                        <IconButton size="small" title="Edit" onClick={() => handleEdit(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title={u.isBlocked ? 'Unblock' : 'Block'}
                          color={u.isBlocked ? 'success' : 'warning'}
                          onClick={() => blockMutation.mutate({ userId: u._id, block: !u.isBlocked })}
                          disabled={blockMutation.isLoading}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Delete"
                          color="error"
                          onClick={() => handleDelete(u)}
                          disabled={deleteMutation.isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <StaffForm
        open={addOpen}
        onClose={() => setAddOpen(false)}
        editingUser={null}
        schoolSubjects={schoolSubjects}
        staffRolesList={staffRolesList}
        authorityLevelsList={authorityLevelsList}
        onSave={handleAddSave}
        isSaving={createMutation.isLoading}
        canEdit={canEdit}
      />

      <StaffForm
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditingUser(null); }}
        editingUser={editingUser}
        schoolSubjects={schoolSubjects}
        staffRolesList={staffRolesList}
        authorityLevelsList={authorityLevelsList}
        onSave={handleEditSave}
        isSaving={updateMutation.isLoading}
        canEdit={canEdit}
      />
    </Box>
  );
}

type StaffSubTab = 'list' | 'subjects' | 'roles' | 'authority';

const Staff: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const perm = useStaffPermissionContext();
  const accessLoading = perm.loading;
  const isPrincipal = perm.isPrincipal;
  const row = perm.accessRow;

  const canListTab = canUseStaffListTab(row, isPrincipal);
  const canSubjectsTab = canUseStaffSubjectsTab(row, isPrincipal);
  const canRolesTab = canUseStaffRolesTab(row, isPrincipal);
  const canAuthorityTab = canUseAuthorityLevelsTab(row, isPrincipal);

  const visibleTabs = React.useMemo(() => {
    const rows: { key: StaffSubTab; label: string; path: string }[] = [];
    if (canListTab) rows.push({ key: 'list', label: 'Staff', path: '/staff' });
    if (canSubjectsTab) rows.push({ key: 'subjects', label: 'Subjects', path: '/staff/subjects' });
    if (canRolesTab) rows.push({ key: 'roles', label: 'Staff roles (what they do)', path: '/staff/roles' });
    if (canAuthorityTab) rows.push({ key: 'authority', label: 'Authority levels (seniority)', path: '/staff/authority-levels' });
    return rows;
  }, [canListTab, canSubjectsTab, canRolesTab, canAuthorityTab]);

  const pathname = location.pathname.replace(/\/$/, '') || '/staff';

  const matchTabPath = React.useCallback((tab: { key: StaffSubTab; path: string }, p: string) => {
    if (tab.key === 'list') return p === '/staff';
    return p === tab.path || p.startsWith(`${tab.path}/`);
  }, []);

  const selectedIndex = React.useMemo(() => {
    const i = visibleTabs.findIndex((t) => matchTabPath(t, pathname));
    return i >= 0 ? i : 0;
  }, [visibleTabs, pathname, matchTabPath]);

  React.useEffect(() => {
    if (accessLoading || visibleTabs.length === 0) return;
    const idx = visibleTabs.findIndex((t) => matchTabPath(t, pathname));
    if (idx < 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [accessLoading, visibleTabs, pathname, navigate, matchTabPath]);

  const activeKey = visibleTabs[selectedIndex]?.key;

  return (
    <>
      <Typography variant="h5" component="h1" gutterBottom>
        Staff
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define school structure with staff roles (what people do) and authority levels (seniority hierarchy), then manage staff and subjects.
      </Typography>
      {accessLoading ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : visibleTabs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No staff sections are available for your account.</Typography>
      ) : (
        <>
          <Tabs
            value={selectedIndex}
            onChange={(_, v) => {
              const t = visibleTabs[v];
              if (t) navigate(t.path);
            }}
            sx={{ mb: 2 }}
          >
            {visibleTabs.map((t) => (
              <Tab key={t.key} label={t.label} />
            ))}
          </Tabs>
          {activeKey === 'list' && <StaffTab />}
          {activeKey === 'subjects' && <SubjectsTab />}
          {activeKey === 'roles' && <StaffRolesTab />}
          {activeKey === 'authority' && <AuthorityLevelsTab />}
        </>
      )}
    </>
  );
};

export default Staff;
